import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { loginWithGoogle } from '../store/slices/authSlice';
import { Card } from '../components/common/Card';
import { Spinner } from '../components/common/Spinner';
import toast from 'react-hot-toast';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/todos');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      // credentialResponse.credential contains the Google ID token
      await dispatch(loginWithGoogle(credentialResponse.credential)).unwrap();
      toast.success('Successfully logged in!');
      navigate('/todos');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Failed to login. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'your-client-id.apps.googleusercontent.com') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-periwinkle-100 via-peach-50 to-mint-100 flex items-center justify-center p-8">
        <Card className="max-w-md w-full p-8 text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Configuration Required</h1>
          <p className="text-slate-600 mb-4">
            Google OAuth is not configured. Please set your Google Client ID in the environment variables.
          </p>
          <div className="bg-slate-100 p-4 rounded-lg text-left">
            <p className="text-sm font-mono text-slate-700">
              1. Go to{' '}
              <a
                href="https://console.cloud.google.com/apis/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="text-periwinkle-600 hover:underline"
              >
                Google Cloud Console
              </a>
            </p>
            <p className="text-sm font-mono text-slate-700 mt-2">
              2. Create OAuth 2.0 credentials
            </p>
            <p className="text-sm font-mono text-slate-700 mt-2">
              3. Add to .env.development:
            </p>
            <p className="text-sm font-mono text-slate-700 mt-1 bg-white p-2 rounded">
              REACT_APP_GOOGLE_CLIENT_ID=your-client-id
            </p>
            <p className="text-sm font-mono text-slate-700 mt-2">
              4. Also update backend .env.development with GOOGLE_CLIENT_ID
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen bg-gradient-to-br from-periwinkle-100 via-peach-50 to-mint-100 flex items-center justify-center p-8">
        <Card className="max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="inline-block p-4 bg-periwinkle-100 rounded-full mb-4">
              <svg
                className="w-12 h-12 text-periwinkle-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Welcome to Looey</h1>
            <p className="text-slate-600">
              Your AI-powered email task assistant
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {typeof error === 'string' ? error : error.message || 'Authentication failed'}
                </div>
              )}

              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                />
              </div>

              <div className="text-center text-sm text-slate-500 mt-6">
                By signing in, you agree to our Terms of Service and Privacy Policy
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600 space-y-2">
              <p className="font-medium">What is Looey?</p>
              <ul className="list-disc list-inside space-y-1 text-slate-500">
                <li>Automatically extracts tasks from your emails</li>
                <li>AI agents execute tasks on your behalf</li>
                <li>Track and manage all your todos in one place</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </GoogleOAuthProvider>
  );
};
